//PDF Renderer
import { Image, Page, Text, StyleSheet, View, Font } from "@react-pdf/renderer";

//Fonts
import montserratRegular from "../../fonts/Montserrat-Regular.ttf";

//Imagenes
import elco from "../../Assets/elco.png";

//Helpers
import {
  existingRenders,
  selectProductRender,
} from "../../Helpers/productSelector";
import { getFechaAlLoTico } from "../../Helpers/date";

Font.register({
  family: "Montserrat-Regular",
  src: montserratRegular,
});

const columnTitles = ["ID. Producto", "Especificaciones", "Anotaciones"];

const styles = StyleSheet.create({
  body: {
    fontFamily: "Montserrat-Regular",
    padding: 20,
  },
  elcoLogo: {
    maxWidth: "100px",
    marginBottom: "20px",
  },
  title: {
    fontSize: 24,
    textAlign: "center",
  },
  productImage: {
    height: "165px",
    width: "165px",
  },
  divider: {
    height: "2px",
    marginTop: "20px",
    marginBottom: "20px",
    backgroundColor: "#000",
  },
  text: {
    fontSize: 11,
  },
  boldText: {
    fontSize: 13,
    fontWeight: "heavy",
  },
  topRightText: {
    position: "absolute",
    top: 20,
    right: 20,
    fontSize: "9.5px",
  },
  table: {
    marginTop: 10,
  },
  headerRow: {
    flexDirection: "row",
    textAlign: "center",
  },
  headerCell: {
    flex: 0.5,
    borderWidth: 1,
    borderColor: "black",
    backgroundColor: "#38BFC2",
    padding: 4,
    color: "white",
    fontSize: "9.5px",
  },

  secondColumnTxt: {
    width: "75%",
  },
  row: {
    flexDirection: "row",
  },
  cell: {
    flex: 0.5,
    borderWidth: 1,
    borderColor: "black",
    padding: 4,
    fontSize: "9px",
    justifyContent: "space-between",
    alignItems: "center",
  },
  amountContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  columnContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
  },
  rowContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginTop: "20px",
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    textAlign: "center",
  },
  checkBox: {
    borderWidth: 1,
    borderColor: "black",
    height: "20px",
    width: "20px",
    marginRight: "10px",
  },
  signatureBox: {
    borderWidth: 1,
    borderColor: "black",
    height: "50px",
    width: "200px",
    marginBottom: "10px",
  },
  signaturesContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginTop: "20px",
    justifyContent: "space-between",
  },
});

export const generateOrderDocument = (data, id, expDate) => {
  // ==========> Component Functions <==========
  //Function to generate product image
  const generateProductImage = (product) => {
    if (existingRenders.includes(product)) {
      return (
        <Image style={styles.productImage} src={selectProductRender(product)} />
      );
    } else {
      return null;
    }
  };

  function groupProductsByAnnotationsAndSpecs() {
    const groupedMap = new Map();

    for (const product of data.products) {
      const { productId, annotations, specifications } = product;
      const key = `${annotations}|||${specifications}`; // clave compuesta única

      if (!groupedMap.has(key)) {
        groupedMap.set(key, {
          products: [],
          annotations,
          specifications,
        });
      }

      groupedMap.get(key).products.push(productId);
    }

    // Convertimos el array de productId en una cadena separada por comas
    const groupedArray = Array.from(groupedMap.values()).map((group) => ({
      products: group.products.join(", "),
      annotations: group.annotations,
      specifications: group.specifications,
    }));

    return groupedArray;
  }

  // ==========> Component <==========
  return (
    <Page style={styles.body}>
      <Image style={styles.elcoLogo} src={elco} />
      <Text style={styles.text}>{`Fecha de creación: ${getFechaAlLoTico(
        new Date(data.createdAt)
      )}`}</Text>
      <Text style={styles.boldText}>{`Cliente: ${data.client}`}</Text>
      <Text style={styles.text}>{`Id del cliente: ${
        data.clientId || "Sin identificador"
      }`}</Text>
      {data.products[0].aditionalData.company && (
        <Text
          style={styles.text}
        >{`Compañía/Agencia que solicita: ${data.products[0].aditionalData.company}`}</Text>
      )}

      <div style={styles.divider}></div>
      <div style={styles.rowContainer}>
        <div>{generateProductImage(data.productName)}</div>

        <div style={styles.columnContainer}>
          <Text style={styles.boldText}>{data.productName}</Text>
          <Text
            style={styles.text}
          >{`Cantidad: ${data.numberOfProducts}`}</Text>
          {data.products[0].aditionalData.connectorType && (
            <Text
              style={styles.text}
            >{`Tipo de conector: ${data.products[0].aditionalData.connectorType}`}</Text>
          )}
          {data.products[0].aditionalData.powerKw && (
            <Text
              style={styles.text}
            >{`Potencia: ${data.products[0].aditionalData.powerKw}kW`}</Text>
          )}
        </div>
      </div>

      <View style={styles.table}>
        <View style={styles.headerRow}>
          {columnTitles.map((title, index) => (
            <View key={index} style={styles.headerCell}>
              <Text>{title}</Text>
            </View>
          ))}
        </View>
        {groupProductsByAnnotationsAndSpecs().map((groupOfProducts) => (
          <View key={groupOfProducts.products} style={styles.row}>
            <View style={styles.cell}>
              <Text>{groupOfProducts.products}</Text>
            </View>
            <View style={styles.cell}>
              <Text>{groupOfProducts.annotations}</Text>
            </View>
            <View style={styles.cell}>
              <Text>{groupOfProducts.specifications}</Text>
            </View>
          </View>
        ))}
      </View>

      <div style={styles.rowContainer}>
        <div style={styles.checkBox}></div>
        <Text
          style={styles.text}
        >{`Se asignaron los identificadores seriales para todos los productos de esta orden`}</Text>
      </div>
      <div style={styles.rowContainer}>
        <div style={styles.checkBox}></div>
        <Text
          style={styles.text}
        >{`Se asignaron los identificadores de módulos de comunicaciones para todos los productos
de esta orden (solo si aplica)
`}</Text>
      </div>

      <div style={styles.signaturesContainer}>
        <div style={styles.columnContainer}>
          <div style={styles.signatureBox}></div>
          <Text style={styles.text}>{`Firma del colaborador encargado`}</Text>
        </div>
        <div style={styles.columnContainer}>
          <div style={styles.signatureBox}></div>
          <Text style={styles.text}>{`Firma del supervisor encargado`}</Text>
        </div>
      </div>

      <div style={styles.divider}></div>
      <div style={styles.footer}>
        <Text style={styles.text}>{`CRC Mechatronics Systems S.A`}</Text>
        <Text style={styles.text}>{`Fecha de impresión: ${getFechaAlLoTico(
          new Date()
        )}`}</Text>
      </div>

      <View style={styles.topRightText}>
        <Text style={styles.boldText}>{`Número de orden ${id}`}</Text>
        <Text style={styles.boldText}>{`Fecha de entrega: ${getFechaAlLoTico(
          new Date(data.dueDate)
        )}`}</Text>
      </View>
    </Page>
  );
};
